import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneIcon } from '@phosphor-icons/react/dist/csr/Phone';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { requestsApi, type CaseCallAction, type CaseCallRow } from '../../lib/api/requests';
import { CALL_ACTION_LABEL_KEY } from './requestLabels';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './LogCallDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * The follow-up call dialog — `POST /case-requests/{request}/calls`
 * (M-09, Task 6, spec D-7, D-8). `RequestList` never mounts this with
 * `open` unless the row's own `next_action` is `first_call`/`second_call`
 * and the session holds `request.call` — the identical belt-and-braces
 * split `SendMessageDialog.tsx`'s own docblock states for `sms.send`.
 *
 * **`kind="form"`** — this is a form the agent came to fill in, titled to
 * name the task ("Log a follow-up call"), never a question: `Dialog`'s own
 * development guard would flag a `'confirm'`-shaped title here (Task 8's
 * `SendMessageDialog` ruling 2, the identical reasoning).
 *
 * **NO `sequence` FIELD, ANYWHERE IN THIS COMPONENT.** The server assigns
 * it (`StoreCaseCallRequest`'s own class docblock: "the single reviewer
 * check is its absence") — this dialog sends only `{action, notes}` and
 * never asks the agent, or itself, which call number this is. A `422`
 * naming the reason (the wait has not elapsed, no resend exists yet, a
 * third call) surfaces exactly as the API worded it, through the same
 * `describeFailure` split every write dialog in this codebase already
 * uses — there is no special-cased "not due yet" message invented here
 * that could drift from the server's own.
 *
 * On success, this component hands the returned `CaseCallRow` straight to
 * `onLogged` and does not itself re-fetch the request — `RequestList`
 * re-reads the ONE row that changed (`requestsApi.get`), the identical
 * "the API already answered the question a refetch would ask again" shape
 * `CaseMessages.tsx` documents for a sent message, adapted to a call:
 * unlike a sent message, `next_action` and `calls` on the OWNING request
 * both need to change, which the standalone `CaseCallRow` this endpoint
 * returns does not itself carry.
 */

export interface LogCallDialogProps {
  open: boolean;
  /** `null` while no row is being acted on — this dialog is not mounted with `open` in that state, but the type still allows it so a caller cannot pass a stale uuid by construction. */
  requestUuid: string | null;
  onClose: () => void;
  onLogged: (call: CaseCallRow) => void;
}

const ACTIONS: readonly CaseCallAction[] = ['reached', 'no_reply', 'unreachable'];

export function LogCallDialog({ open, requestUuid, onClose, onLogged }: LogCallDialogProps) {
  const { t } = useTranslation();

  const [action, setAction] = useState<CaseCallAction>('reached');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAction('reached');
    setNotes('');
    setFailure(null);
    setFieldErrors({});
  }, [open]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('requests.callFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current || requestUuid === null) return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setFieldErrors({});

    try {
      const logged = await requestsApi.logCall(requestUuid, {
        action,
        notes: notes.trim() === '' ? null : notes.trim(),
      });
      onLogged(logged);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;

      const mapped = mapDetails(error.details);

      /*
       * THE SERVER'S REFUSAL IS NOT KEYED TO A FIELD ON THIS FORM, and that is
       * the whole reason this branch exists.
       *
       * `CaseCallController` refuses an out-of-turn call with
       * `ValidationException::withMessages(['call' => [reason]])` — "no resend
       * yet", "already logged", "already reached", "the wait has not elapsed".
       * The form renders `action` and `notes`; `call` is neither, so the
       * message went into `fieldErrors` and was displayed nowhere. The dialog
       * stayed open with no explanation of any kind.
       *
       * Anything that does not name a field this form renders is shown in the
       * failure banner instead of being dropped on the floor.
       */
      const banner = Object.entries(mapped)
        .filter(([field]) => field !== 'action' && field !== 'notes')
        .map(([, message]) => message);

      setFieldErrors(mapped);

      if (banner.length > 0) {
        setFailure(banner.join(' '));
      } else if (Object.keys(mapped).length === 0) {
        setFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const options: readonly SelectOption[] = ACTIONS.map((value) => ({
    value,
    label: t(CALL_ACTION_LABEL_KEY[value]),
  }));

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('requests.logCallTitle')}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <Select
          label={t('requests.callActionLabel')}
          width="compact"
          required
          options={options}
          value={action}
          error={fieldErrors['action']}
          onChange={(event) => {
            const { value } = event.target;
            setAction(value as CaseCallAction);
          }}
        />

        <Textarea
          label={t('requests.callNotesLabel')}
          dir="auto"
          value={notes}
          error={fieldErrors['notes']}
          onChange={(event) => {
            const { value } = event.target;
            setNotes(value);
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<PhoneIcon size={16} />}>
            {t('requests.logCallSubmit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('cases.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
